import React, { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useApplicationContext } from '../context/ApplicationContext';
import { toast } from 'react-toastify';
import config from '../config';
import ImageCropperModal from './common/ImageCropperModal';
import { toLocalInputValue, fromLocalInputValue } from '../utils/dateInput';
import { formatDisplayDate } from '../utils/formatDate';

const VenueBlogsManager = ({ venueId, isOwner = false }) => {
    const { auth } = useAuth();
    const navigate = useNavigate();
    const { setActiveTabId } = useApplicationContext();
    
    const [blogs, setBlogs] = useState([]);
    const [limits, setLimits] = useState({ max: 0, current: 0 });
    const [analytics, setAnalytics] = useState({
        total_posts: 0,
        published_posts: 0,
        draft_posts: 0,
        archived_posts: 0,
        total_likes: 0,
        total_favorites: 0,
        total_engagements: 0
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [editingBlog, setEditingBlog] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    
    // New blog form
    const [showAddForm, setShowAddForm] = useState(false);
    const [newBlog, setNewBlog] = useState({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        featured_image: '',
        status: 'draft',
        expires_at: ''
    });

    // Cropper State
    const [cropperState, setCropperState] = useState({
        show: false,
        imageSrc: null,
        fixedAspectRatio: 3/2, // 3:2 for Feed/Blog
        initialAspectRatio: 3/2,
        minWidth: 750
    });

    // Handle file selection (opens cropper)
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file type
        if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
             toast.error('Only JPG, PNG, and WebP are allowed');
             return;
        }

        // Check file size (10MB source limit for cropper)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }
        
        // Create object URL for cropper
        const objectUrl = URL.createObjectURL(file);
        
        setCropperState({
            show: true,
            imageSrc: objectUrl,
            fixedAspectRatio: 3/2,
            initialAspectRatio: 3/2,
            minWidth: 750
        });
        
        // Reset input
        e.target.value = '';
    };

    const handleCropperHide = () => {
        if (cropperState.imageSrc) {
            URL.revokeObjectURL(cropperState.imageSrc);
        }
        setCropperState(prev => ({ ...prev, show: false, imageSrc: null }));
    };

    const handleCropComplete = (croppedFile, previewUrl) => {
        if (editingBlog) {
            setEditingBlog(prev => ({
                ...prev,
                featured_image_file: croppedFile,
                featured_image_preview: previewUrl
            }));
        } else {
            setNewBlog(prev => ({
                ...prev,
                featured_image_file: croppedFile,
                featured_image_preview: previewUrl
            }));
        }
        
        // Cleanup cropper source
        if (cropperState.imageSrc) {
            URL.revokeObjectURL(cropperState.imageSrc);
        }
        setCropperState(prev => ({ ...prev, show: false, imageSrc: null }));
    };

    // Fetch blogs
    const fetchBlogs = useCallback(async () => {
        try {
            const endpoint = isOwner 
                ? `${config.API_BASE_URL}/api/venues/${venueId}/blogs`
                : `${config.API_BASE_URL}/api/public/venues/${venueId}/blogs`;

            const response = await auth.fetch(endpoint);
            const data = await response.json();
            
            if (response.ok) {
                // Backend now returns { blogs: [], limit: N }
                // Handle both new format and potential legacy format (just array)
                let blogList = Array.isArray(data) ? data : (data.blogs || []);
                const limitValue = !Array.isArray(data) && data.limit !== undefined ? data.limit : 0;
                
                // Normalize data
                blogList = blogList.map(blog => ({
                    ...blog,
                    image_url: blog.image_url || blog.featured_image, // Handle both
                    status: blog.status || 'published' // Default to published for public view
                }));

                setBlogs(blogList);
                if (isOwner && data.analytics) {
                    setAnalytics(data.analytics);
                }
                setLimits({
                    max: limitValue,
                    current: blogList.length
                });
            }
        } catch (error) {
            console.error('Error fetching blogs:', error);
        } finally {
            setLoading(false);
        }
    }, [auth, venueId, isOwner]);

    // Fetch limits separately if not in blog response
    const fetchLimits = useCallback(async () => {
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/limits?venue_id=${venueId}`);
            const data = await response.json();
            
            if (response.ok && data.limits?.blogs) {
                setLimits({
                    max: data.limits.blogs.max,
                    current: data.limits.blogs.current
                });
            }
        } catch (error) {
            console.error('Error fetching limits:', error);
        }
    }, [auth, venueId]);

    useEffect(() => {
        if (venueId) {
            fetchBlogs();
            fetchLimits();
        }
    }, [venueId, fetchBlogs, fetchLimits]);

    // Get remaining slots
    const getRemainingSlots = () => {
        return Math.max(0, limits.max - limits.current);
    };

    const handleUpgrade = (e) => {
        if (e) e.preventDefault();
        setActiveTabId('my-plan');
        navigate('/applications');
    };

    // Generate slug from title
    const generateSlug = (title) => {
        return title
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    };

    // Handle input change for new blog
    const handleNewBlogChange = (e) => {
        const { name, value } = e.target;
        setNewBlog(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'title') {
                updated.slug = generateSlug(value);
            }
            return updated;
        });
    };

    // Handle input change for editing blog
    const handleEditBlogChange = (e) => {
        const { name, value } = e.target;
        setEditingBlog(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'title') {
                updated.slug = generateSlug(value);
            }
            return updated;
        });
    };

    // Add new blog
    const handleAddBlog = async (e) => {
        e.preventDefault();
        
        if (!newBlog.title.trim()) {
            toast.error('Blog title is required');
            return;
        }
        
        if (getRemainingSlots() <= 0) {
            toast.error('You have reached the maximum number of blogs allowed');
            return;
        }
        
        setSaving(true);
        
        try {
            const formData = new FormData();
            formData.append('title', newBlog.title);
            formData.append('slug', newBlog.slug);
            formData.append('excerpt', newBlog.excerpt);
            formData.append('content', newBlog.content);
            formData.append('status', newBlog.status);
            formData.append('expires_at', fromLocalInputValue(newBlog.expires_at));
            
            if (newBlog.featured_image_file) {
                formData.append('featured_image', newBlog.featured_image_file);
            } else if (newBlog.featured_image) {
                // If user somehow entered a URL manually (if we kept the input as fallback, but we'll remove it)
                // But since we are changing input type to file, this might not be needed unless we support both.
                // For now, let's support it if it's there.
                formData.append('featured_image', newBlog.featured_image);
            }

            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/blogs`, {
                method: 'POST',
                body: formData
            });
            
            const data = await response.json();
            
            if (response.ok) {
                toast.success('Blog post created successfully');
                setNewBlog({
                    title: '',
                    slug: '',
                    excerpt: '',
                    content: '',
                    featured_image: '',
                    status: 'draft',
                    expires_at: ''
                });
                setShowAddForm(false);
                fetchBlogs();
                fetchLimits();
            } else {
                if (data.error === 'SUBSCRIPTION_LIMIT_EXCEEDED') {
                    toast.error(data.message);
                } else {
                    toast.error(data.message || 'Failed to create blog');
                }
            }
        } catch (error) {
            console.error('Error creating blog:', error);
            toast.error('Failed to create blog');
        } finally {
            setSaving(false);
        }
    };

    // Update blog
    const handleUpdateBlog = async (e) => {
        e.preventDefault();
        
        if (!editingBlog.title.trim()) {
            toast.error('Blog title is required');
            return;
        }
        
        setSaving(true);
        
        try {
            const formData = new FormData();
            formData.append('title', editingBlog.title);
            formData.append('slug', editingBlog.slug);
            formData.append('excerpt', editingBlog.excerpt);
            formData.append('content', editingBlog.content);
            formData.append('status', editingBlog.status);
            formData.append('expires_at', fromLocalInputValue(editingBlog.expires_at));
            
            if (editingBlog.featured_image_file) {
                formData.append('featured_image', editingBlog.featured_image_file);
            } else if (editingBlog.featured_image) {
                formData.append('featured_image', editingBlog.featured_image);
            }

            const response = await auth.fetch(`${config.API_BASE_URL}/api/venues/${venueId}/blogs/${editingBlog.id}`, {
                method: 'PUT',
                body: formData
            });
            
            if (response.ok) {
                toast.success('Blog updated successfully');
                setEditingBlog(null);
                fetchBlogs();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to update blog');
            }
        } catch (error) {
            console.error('Error updating blog:', error);
            toast.error('Failed to update blog');
        } finally {
            setSaving(false);
        }
    };

    // Delete blog
    const handleDeleteBlog = async (blogId) => {
        if (!window.confirm('Are you sure you want to delete this blog post?')) return;
        
        setDeletingId(blogId);
        
        try {
            const response = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}/blogs/${blogId}`, {
                method: 'DELETE'
            });
            
            if (response.ok) {
                toast.success('Blog deleted successfully');
                fetchBlogs();
                fetchLimits();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to delete blog');
            }
        } catch (error) {
            console.error('Error deleting blog:', error);
            toast.error('Failed to delete blog');
        } finally {
            setDeletingId(null);
        }
    };

    // Get status badge color
    const getStatusBadge = (status) => {
        switch (status) {
            case 'published':
                return { cls: 'hs-badge-success', icon: 'solar:check-circle-bold', label: 'Published' };
            case 'draft':
                return { cls: 'hs-badge-warning', icon: 'solar:document-bold', label: 'Draft' };
            case 'archived':
                return { cls: 'hs-badge-neutral', icon: 'solar:archive-bold', label: 'Archived' };
            default:
                return { cls: 'hs-badge-neutral', icon: 'solar:info-circle-bold', label: status };
        }
    };

    if (loading) {
        return (
            <div className="hs-card p-0">
                <div className="hs-card-body d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading feed posts...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="hs-card">
            <div className="hs-card-header">
                <div className="d-flex align-items-center gap-2">
                    <Icon icon="solar:document-text-bold" className="text-primary-600 text-xl" />
                    <h6 className="fw-bold mb-0 text-dark">Venue Feed & Announcements</h6>
                </div>
                {isOwner && (
                    <div className="d-flex align-items-center gap-12">
                        <span className="hs-badge hs-badge-info">
                            {limits.max === 0 ? 'Not included in plan' : `${limits.current} / ${limits.max} posts`}
                        </span>
                        {getRemainingSlots() > 0 && !showAddForm && !editingBlog && (
                            <button
                                className="hs-btn-primary hs-btn-sm"
                                onClick={() => setShowAddForm(true)}
                            >
                                <Icon icon="solar:add-circle-bold" />
                                Create Post
                            </button>
                        )}
                    </div>
                )}
            </div>
        
        <div className="card-body p-24">
                {isOwner && (
                    <div className="row g-16 mb-24" aria-label="Feed engagement analytics">
                        {[
                            { label: 'Feed posts', value: analytics.total_posts, icon: 'mdi:post-outline', color: 'primary' },
                            { label: 'Likes', value: analytics.total_likes, icon: 'mdi:heart-outline', color: 'danger' },
                            { label: 'Favorites', value: analytics.total_favorites, icon: 'mdi:bookmark-outline', color: 'warning' },
                            { label: 'Total engagement', value: analytics.total_engagements, icon: 'mdi:chart-line', color: 'success' }
                        ].map((metric) => (
                            <div className="col-6 col-lg-3" key={metric.label}>
                                <div className="border radius-8 p-16 h-100 d-flex align-items-center gap-12">
                                    <span className={`w-40-px h-40-px bg-${metric.color}-100 text-${metric.color}-600 rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0`}>
                                        <Icon icon={metric.icon} className="text-xl" />
                                    </span>
                                    <div>
                                        <p className="text-secondary-light text-sm mb-2">{metric.label}</p>
                                        <h6 className="mb-0 fw-bold">{Number(metric.value) || 0}</h6>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

            {/* Limit Exceeded/Not Available Alert */}
            {isOwner && limits.max === 0 ? (
                <div className="alert alert-secondary mb-24 bg-neutral-100 border-neutral-200 text-neutral-600">
                    <div className="d-flex align-items-center">
                        <Icon icon="mdi:lock" className="text-xl me-12" />
                        <div>
                            <p className="fw-semibold mb-0">Blog Feature Not Available</p>
                            <p className="text-sm mb-0">
                                Your current plan does not include blog posts. 
                                <button type="button" onClick={handleUpgrade} className="p-0 border-0 bg-transparent ms-4 text-primary-600 fw-medium">Upgrade to add this feature</button>
                            </p>
                        </div>
                    </div>
                </div>
            ) : isOwner && getRemainingSlots() <= 0 && !showAddForm && !editingBlog && (
                    <div className="alert alert-warning mb-24 d-flex align-items-center">
                    <Icon icon="mdi:alert" className="me-12 text-xl" />
                    <div>
                        You have reached the maximum number of blog posts ({limits.max}) allowed by your subscription plan.
                        <button type="button" onClick={handleUpgrade} className="p-0 border-0 bg-transparent ms-8 fw-semibold text-decoration-underline">Upgrade your plan</button>
                    </div>
                </div>
            )}

                {/* Add/Edit Blog Form */}
                {(showAddForm || editingBlog) && (
                    <form 
                        onSubmit={editingBlog ? handleUpdateBlog : handleAddBlog} 
                        className="bg-neutral-50 radius-12 p-24 mb-24"
                    >
                        <h6 className="text-md fw-semibold mb-16">
                            {editingBlog ? 'Edit Blog Post' : 'Create New Post'}
                        </h6>
                        
                        <div className="row g-16">
                            <div className="col-md-8">
                                <label className="form-label text-sm fw-medium">
                                    Title <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control radius-8"
                                    name="title"
                                    value={editingBlog ? editingBlog.title : newBlog.title}
                                    onChange={editingBlog ? handleEditBlogChange : handleNewBlogChange}
                                    placeholder="Blog post title"
                                    maxLength={200}
                                />
                            </div>
                            
                            <div className="col-md-4">
                                <label className="form-label text-sm fw-medium">Status</label>
                                <select
                                    className="form-select radius-8"
                                    name="status"
                                    value={editingBlog ? editingBlog.status : newBlog.status}
                                    onChange={editingBlog ? handleEditBlogChange : handleNewBlogChange}
                                >
                                    <option value="draft">Draft</option>
                                    <option value="published">Published</option>
                                    <option value="archived">Archived</option>
                                </select>
                            </div>
                            
                            <div className="col-md-8">
                                <label className="form-label text-sm fw-medium">Slug</label>
                                <input
                                    type="text"
                                    className="form-control radius-8"
                                    name="slug"
                                    value={editingBlog ? editingBlog.slug : newBlog.slug}
                                    onChange={editingBlog ? handleEditBlogChange : handleNewBlogChange}
                                    placeholder="blog-post-url-slug"
                                />
                                <small className="text-secondary-light">URL-friendly version of the title</small>
                            </div>

                            <div className="col-md-4">
                                <label className="form-label text-sm fw-medium">Expires (optional)</label>
                                <input
                                    type="datetime-local"
                                    className="form-control radius-8"
                                    name="expires_at"
                                    value={editingBlog ? editingBlog.expires_at || '' : newBlog.expires_at}
                                    onChange={editingBlog ? handleEditBlogChange : handleNewBlogChange}
                                />
                                <small className="text-secondary-light">Hidden from What's On after this time</small>
                            </div>
                            
                            <div className="col-md-4">
                                <label className="form-label text-sm fw-medium">Featured Image</label>
                                <input
                                    type="file"
                                    className="form-control radius-8"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                                <small className="text-secondary-light">Max 2MB. Leave empty to keep current image.</small>
                                {(editingBlog?.featured_image || editingBlog?.featured_image_preview || newBlog.featured_image_preview) && (
                                    <div className="mt-2">
                                        <img 
                                            src={editingBlog?.featured_image_preview || newBlog.featured_image_preview || (editingBlog?.featured_image?.startsWith('http') ? editingBlog.featured_image : `${config.API_BASE_URL}/${editingBlog.featured_image?.replace(/^\//, '')}`)} 
                                            alt="Preview" 
                                            className="img-thumbnail"
                                            style={{ height: '100px', objectFit: 'cover' }}
                                        />
                                    </div>
                                )}
                            </div>
                            
                            <div className="col-12">
                                <label className="form-label text-sm fw-medium">Excerpt</label>
                                <textarea
                                    className="form-control radius-8"
                                    name="excerpt"
                                    value={editingBlog ? editingBlog.excerpt : newBlog.excerpt}
                                    onChange={editingBlog ? handleEditBlogChange : handleNewBlogChange}
                                    placeholder="Short description for previews"
                                    rows={2}
                                    maxLength={500}
                                />
                            </div>
                            
                            <div className="col-12">
                                <label className="form-label text-sm fw-medium">Content</label>
                                <textarea
                                    className="form-control radius-8"
                                    name="content"
                                    value={editingBlog ? editingBlog.content : newBlog.content}
                                    onChange={editingBlog ? handleEditBlogChange : handleNewBlogChange}
                                    placeholder="Full blog post content..."
                                    rows={8}
                                />
                            </div>
                            
                            <div className="col-12">
                                <div className="d-flex gap-12 justify-content-end">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary radius-8 px-16"
                                        onClick={() => {
                                            setShowAddForm(false);
                                            setEditingBlog(null);
                                            setNewBlog({
                                                title: '',
                                                slug: '',
                                                excerpt: '',
                                                content: '',
                                                featured_image: '',
                                                status: 'draft'
                                            });
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary radius-8 px-24"
                                        disabled={saving}
                                    >
                                        {saving ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-8" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                <Icon icon="mdi:content-save" className="me-8" />
                                                {editingBlog ? 'Update Post' : 'Create Post'}
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                )}
                
                {/* Blog Posts List */}
                {blogs.length > 0 ? (
                    <div className="row g-20">
                        {blogs.map((blog) => {
                            const statusObj = getStatusBadge(blog.status);
                            return (
                                <div key={blog.id} className="col-md-6 col-lg-4">
                                    <div className="hs-card hs-card-hover h-100 d-flex flex-column">
                                        <div className="position-relative bg-neutral-100" style={{ height: '160px', overflow: 'hidden' }}>
                                            {blog.image_url ? (
                                                <img
                                                    src={blog.image_url.startsWith('http') ? blog.image_url : `${config.API_BASE_URL}/${blog.image_url.replace(/^\//, '')}`}
                                                    alt={blog.title}
                                                    className="w-100 h-100 object-fit-cover"
                                                />
                                            ) : (
                                                <div className="w-100 h-100 d-flex align-items-center justify-content-center text-secondary-light">
                                                    <Icon icon="solar:gallery-bold" className="text-4xl" />
                                                </div>
                                            )}
                                            <div className="position-absolute top-12 inset-inline-end-12">
                                                <span className={`hs-badge ${statusObj.cls}`}>
                                                    <Icon icon={statusObj.icon} className="text-xs" />
                                                    {statusObj.label}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="hs-card-body p-16 flex-grow-1 d-flex flex-column">
                                            <div className="text-xs text-secondary-light mb-6 d-flex align-items-center gap-1">
                                                <Icon icon="solar:calendar-bold" className="text-xs" />
                                                {formatDisplayDate(blog.created_at)}
                                            </div>
                                            <h6 className="fw-bold text-dark mb-8 line-clamp-2">
                                                {blog.title}
                                            </h6>
                                            {blog.excerpt && (
                                                <p className="text-secondary-light text-xs mb-12 line-clamp-2 flex-grow-1">
                                                    {blog.excerpt}
                                                </p>
                                            )}
                                            {isOwner && (
                                                <div className="d-flex align-items-center justify-content-between pt-10 border-top mt-auto">
                                                    <div className="d-flex align-items-center gap-10">
                                                        <span className="text-xs text-secondary-light d-flex align-items-center gap-1">
                                                            <Icon icon="solar:heart-bold" className="text-danger-600" />
                                                            {Number(blog.like_count) || 0}
                                                        </span>
                                                        <span className="text-xs text-secondary-light d-flex align-items-center gap-1">
                                                            <Icon icon="solar:bookmark-bold" className="text-warning-600" />
                                                            {Number(blog.favorite_count) || 0}
                                                        </span>
                                                    </div>
                                                    <div className="d-flex align-items-center gap-6">
                                                        <button
                                                            type="button"
                                                            className="hs-icon-btn hs-btn-sm"
                                                            onClick={() => setEditingBlog({ ...blog, featured_image: blog.image_url, expires_at: toLocalInputValue(blog.expires_at) })}
                                                            title="Edit Post"
                                                        >
                                                            <Icon icon="solar:pen-bold" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="hs-icon-btn hs-btn-sm text-danger-600"
                                                            onClick={() => handleDeleteBlog(blog.id)}
                                                            disabled={deletingId === blog.id}
                                                            title="Delete Post"
                                                        >
                                                            {deletingId === blog.id ? (
                                                                <span className="spinner-border spinner-border-sm" />
                                                            ) : (
                                                                <Icon icon="solar:trash-bin-trash-bold" />
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="hs-empty-state">
                        <div className="hs-empty-icon">
                            <Icon icon="solar:document-text-bold" />
                        </div>
                        <h5 className="hs-empty-title">No Feed Posts Published</h5>
                        <p className="hs-empty-desc">
                            Share weekly specials, events, chef highlights, or updates directly to the mobile app feed.
                        </p>
                        {getRemainingSlots() > 0 && !showAddForm && (
                            <button
                                className="hs-btn-primary"
                                onClick={() => setShowAddForm(true)}
                            >
                                <Icon icon="solar:add-circle-bold" />
                                Create First Post
                            </button>
                        )}
                    </div>
                )}

                {/* Image Cropper Modal */}
                {cropperState.show && (
                    <ImageCropperModal
                        show={cropperState.show}
                        onHide={handleCropperHide}
                        imageSrc={cropperState.imageSrc}
                        onCropComplete={handleCropComplete}
                        fixedAspectRatio={cropperState.fixedAspectRatio}
                        initialAspectRatio={cropperState.initialAspectRatio}
                        minWidth={cropperState.minWidth}
                    />
                )}
            </div>
        </div>
    );
};

export default VenueBlogsManager;
